// @flow
import * as React from 'react';

import { useDispatch } from 'react-redux';

import { Link } from 'react-router-dom';
// eslint-disable-next-line import/no-extraneous-dependencies
import { createFilter } from 'react-select';

import { useEntityPermissions } from '@gandi/acl';
import { selectIsSamlUser } from '@gandi/authentication';
import {
  selectIsLoadedCartOwnerList as selectLoadedCartOwners,
  selectIsFailureCartOwnerList as selectFailureCartOwners,
  selectIsFetchingCartOwnerList as selectFetchingCartOwners,
  fetchCartOwnerList as fetchCartOwners,
  selectCartUUID as selectCartId,
  selectHasOwnerOnly,
} from '@gandi/cart';
import { CountryName } from '@gandi/react-places';
import {
  selectPersonalId,
  selectCurrentId,
  type Organization,
} from '@gandi/redux-sharing';
import { useTranslator } from '@gandi/translate';
import { Base } from '@gandi/ui';
import { Custom } from '@gandi/ui-select';

import { useCartSummaryContext } from '../../../Layout/context/CartSummary';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { ORGA_WRITE } from '../../../modules/permissions';
import type {
  ComponentWithPrefetch,
  FullState,
  PreFetchArgs,
} from '../../../types';
import { selectCartOwners } from '../modules/selectors';

import type { CartOwnerCandidate } from '../types';

import { getDisplayName, Option } from './Option';

import styles from './PayerSelect.scss';

type OwnProps = {
  onOwnerChange: (orgId: string) => void,
  frozen: boolean,
};

export type Props = {
  ...OwnProps,
};

function stringify({ data }: any): string {
  return Object.keys(data)
    .filter(key => data[key] && typeof data[key] === 'string')
    .reduce((acc, key) => {
      // default case
      let substring = data[key];

      // Handle firstname / lastname semantic groups
      if (key === 'firstname') {
        substring = `${acc}${data.firstname} ${data.lastname}`;
      }
      if (key === 'lastname') {
        substring = `${acc}${data.lastname} ${data.firstname}`;
      }

      return `${acc}${substring} `;
    }, '');
}

function getOrgEdit(org: string, cart: string) {
  return `/cart/${cart}/configure/account/${org}?owner=cart`;
}

function getId(org: any): string {
  return org.id || '';
}

function renderOption(org: CartOwnerCandidate | Organization): React.Node {
  return <Option key={org.id} isPayer org={org} />;
}

function onChange(
  callback: (id: string) => void,
  current: null | CartOwnerCandidate,
  update: (next: null | CartOwnerCandidate) => any,
  next_: any,
) {
  const next = Array.isArray(next_) ? next_[0] : next_;
  if (next !== current) {
    update(next);
    if (typeof callback === 'function') {
      callback(next.id);
    }
  }
}

export function PayerSelect({
  frozen,
  onOwnerChange,
}: Props): React.MixedElement {
  const translator = useTranslator();
  const dispatch = useDispatch();
  const { __ } = translator;

  const { owner, setOwner } = useCartSummaryContext();

  const [mounted, setMounted] = React.useState(false);

  const fetchingCartOwners = useAppSelector(selectFetchingCartOwners);
  const loadedCartOwners = useAppSelector(selectLoadedCartOwners);
  const failureCartOwners = useAppSelector(
    state => !!selectFailureCartOwners(state),
  );
  const cartOwners = useAppSelector(selectCartOwners);
  const cartId = useAppSelector(selectCartId);
  const personalId = useAppSelector(selectPersonalId);
  const currentId = useAppSelector(selectCurrentId);

  const hasOwnerOnly = useAppSelector(selectHasOwnerOnly);
  const isSamlUser = useAppSelector(selectIsSamlUser);

  const defaultOwner = cartOwners[0];

  const { hasPermissions, isLoadedPermissions, isFetchingPermissions } =
    useEntityPermissions(owner?.id || 'no-owner-yet');

  // TODO: migrate to @gandi/resource
  const shouldFetch =
    !fetchingCartOwners &&
    ((!loadedCartOwners && !failureCartOwners) || !mounted) &&
    cartOwners.length === 0;

  React.useEffect(() => {
    if (shouldFetch) {
      dispatch(fetchCartOwners());
    }
    // Other dependencies don't matter here, we need to use the version of them
    // that's available via closure when the hook is run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetch]);

  React.useEffect(() => {
    if (cartOwners.length) {
      const currentCartOwner = cartOwners.find(
        cartOwner => cartOwner.id === currentId,
      );

      setOwner(currentCartOwner || defaultOwner);
    }
  }, [cartOwners, currentId, defaultOwner, setOwner]);

  /* --------------- Force a re-render after hydration/mount. --------------- */
  // Required to mitigate a SSR/hydration bug in Safari
  React.useEffect(() => {
    if (!mounted) {
      setMounted(true);
    }
  }, [mounted, setMounted]);
  /* ------------------------------------------------------------------------ */

  const candidates = React.useMemo(
    () =>
      [
        cartOwners.find(candidate => candidate.id === personalId),
        ...cartOwners
          .filter(candidate => candidate.id !== personalId)
          .sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b))),
      ].filter(Boolean),
    [cartOwners, personalId],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleChange = React.useCallback(
    onChange.bind(null, onOwnerChange, owner, setOwner),
    [owner, setOwner],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filterOpt = React.useCallback(createFilter({ stringify }), []);

  const renderNoOpt = React.useCallback(
    () => __('Only organizations eligible for this order are listed'),
    [__],
  );

  return (
    <Base flex flexCol>
      <div className={styles.container}>
        <Custom
          id="payer-select-container"
          inputId="payer-select-input"
          instanceId="payer-select-instance"
          translator={translator}
          name="payer"
          isClearable={false}
          isDisabled={
            fetchingCartOwners || failureCartOwners || !mounted || frozen
          }
          value={owner}
          options={candidates}
          getOptionValue={getId}
          filterOption={filterOpt}
          renderOption={renderOption}
          onChange={handleChange}
          maxMenuHeight={500}
          noOptionsMessage={renderNoOpt}
        />
      </div>
      <span className={styles.linksContainer}>
        {owner &&
          isLoadedPermissions &&
          !isFetchingPermissions &&
          hasPermissions([ORGA_WRITE]) && (
            <>
              <Link to={getOrgEdit(owner.id, cartId)}>
                {translator.__('Edit %(name)s', {
                  name: getDisplayName(owner),
                })}
              </Link>
              {!hasOwnerOnly && !isSamlUser && (
                <hr className={styles.separator} />
              )}
            </>
          )}
        {!hasOwnerOnly && !isSamlUser && (
          <Link to={`/cart/${cartId}/configure/account/new?owner=cart`}>
            {__('Create New')}
          </Link>
        )}
      </span>
    </Base>
  );
}

PayerSelect.preFetch = (
  args: PreFetchArgs<{}, any, any, FullState>,
): Promise<any> => {
  const { dispatch, getState } = args;
  const loadedCartOwners = selectLoadedCartOwners(getState());
  return Promise.allSettled(
    [
      CountryName.preFetch({ dispatch, getState }).catch(() => {}),
      !loadedCartOwners && dispatch(fetchCartOwners()),
    ].filter(Boolean),
  );
};

export default (PayerSelect: ComponentWithPrefetch<
  OwnProps,
  typeof PayerSelect.preFetch,
>);
